import React, {useState, useRef, useEffect} from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import {Line} from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import _ from "lodash";
import m from "moment";
import pattern from "patternomaly";
import Calculations from '../../util/Calculations';
import Timeline from "../Timeline";
import * as ChartLabels from './ChartLabels';
import {sleepBarTooltip, presenceBarTooltip} from "components/Tooltips/PatientChartTooltips";
import {WhisperWrapper} from "components/Tooltips/WhisperWrapper";

ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    zoomPlugin
);

export default function PatientChart(props){
    const [loading, setLoading] = useState(true);
    const [patientChartArea, setPatientChartArea] = useState({padding: 20, left: 0, width: 600});
    const chartRef = useRef(ChartJS);
    const outOfRangeLegendRef = React.createRef();
    const {startTime, endTime, rangeOOOTransitions} = props;
    const mEndTime = m(endTime);
    const mStartTime = m(startTime);
    const dateRangeLength = Math.round(mEndTime.diff(mStartTime, "d", true));
    const shortRange = Math.round(mEndTime.diff(mStartTime, "h", true)) < 4;
    const _endTime = shortRange ? endTime : mEndTime.add(30, "m").toDate();

    useEffect(() => {
        const chart = chartRef.current;
        getChartArea(chart);
        drawOutOfRangeLegend();
    }, []);
    function getChartArea(chart){
        const area = chart.instances[1].scales;
        setPatientChartArea({padding: 20, left: area.x.left, width: area.x.width});
    }
    function drawOutOfRangeLegend(){
        if (outOfRangeLegendRef.current) {
            const ctx = outOfRangeLegendRef.current.getContext("2d");
            ctx.fillStyle = pattern.draw("diagonal", "#CCCCCC");
            ctx.fillRect(0, 0, 20, 20);
        }
    }
    const respirationLowError = Calculations.calculateLowerPoints(
        props.respiration,
        props.SettingResp.normal_low
    );
    const respirationHighError = Calculations.calculateUpperPoints(
        props.respiration,
        props.SettingResp.normal_high
    );

    const graphOptions = {
        responsive: true,
        parsing: {
            xAxisKey: 't',
            yAxisKey: 'y'
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        stacked: false,
        plugins: {
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                callbacks: {
                    // Filter out tooltips for hidden datasets
                    filter: function(tooltipItem) {
                        // Don't show tooltips for the background ranges
                        if (tooltipItem.dataset.label.includes(ChartLabels.labels.normalRangeLowerBound.value) ||
                            tooltipItem.dataset.label.includes(ChartLabels.labels.normalRangeUpperBound.value) ||
                            tooltipItem.dataset.label.includes(ChartLabels.labels.outOfRange.value)) {
                            return false;
                        }
                        return true;
                    }
                }
            },
            datalabels: {
                display: false
            },
            legend: {
                display: false,
                position: 'top',
                labels: {
                    filter: (filterItem) => !ChartLabels.labelBlackList.find((label) => {
                        const text = filterItem.label || filterItem.text;
                        return text.indexOf(label) > -1;
                    }),
                }
            }
        },
        scales: {
            x: {
                type: "time",
                time: {
                    displayFormats: {
                        minute: "h:mmA",
                        hour:
                            dateRangeLength > 0 && dateRangeLength <= 2
                                ? "M/D/YY h:mmA"
                                : "h:mmA",
                        day: "M/D/YY hA"
                    },
                    unit:
                        dateRangeLength > 2 ? "day" : shortRange ? "minute" : "hour" // eslint-disable-line no-nested-ternary
                },
                display: true,
                ticks: {
                    display: true
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'RR',
                    font: {
                        family: "Oswald",
                        size: "18",
                        weight: "bold"
                    }
                },
                ticks: {
                    min: 0,
                    max: 30,
                    display: true,
                    callback: function(value, index, values) {
                        // Only show y-axis values, not data point values
                        return value;
                    }
                }
            },
            y1: {
                id: "amplitude",
                type: 'linear',
                display: true,
                beginAtZero: true,
                position: 'right',
                grid: {
                    drawOnChartArea: false,
                },
                title: {
                    display: true,
                    text: 'Movement',
                    font: {
                        family: "Oswald",
                        size: "18",
                        weight: "bold"
                    }
                },
                ticks: {
                    min: 0,
                    max: 100,
                    stepSize: 10,
                    display: true,
                    callback: function(value, index, values) {
                        // Only show y-axis values, not data point values
                        return value;
                    }
                }
            },
        },
    };

    const graphData = {datasets: []};

    // Create a single dataset for the normal range with fill=true
    graphData.datasets.push({
        data: [
            {
                t: props.startTime,
                y: props.SettingResp.normal_low
            },
            {
                t: _endTime,
                y: props.SettingResp.normal_low
            }
        ],
        label: ChartLabels.labels.normalRangeLowerBound.value,
        yAxisID: "y",
        borderColor: "#FFFFFF",
        borderWidth: 0,
        fill: false,
        backgroundColor: "#FFFFFF",
        pointRadius: 0,
        pointBorderColor: 'rgba(0, 0, 0, 0)',
        pointBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBorderColor: 'rgba(0, 0, 0, 0)'
    });

    graphData.datasets.push({
        data: [
            {
                t: props.startTime,
                y: props.SettingResp.normal_high
            },
            {
                t: _endTime,
                y: props.SettingResp.normal_high
            }
        ],
        label: ChartLabels.labels.normalRangeUpperBound.value,
        yAxisID: "y",
        borderColor: "#FFFFFF",
        borderWidth: 0,
        fill: "-1",
        backgroundColor: "#FFFFFF",
        pointRadius: 0,
        pointBorderColor: 'rgba(0, 0, 0, 0)',
        pointBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBorderColor: 'rgba(0, 0, 0, 0)'
    });

    _.chunk(rangeOOOTransitions, 2).map(([start, end]) => {
        graphData.datasets.push(
            {
                data: [
                    {t: start.timestamp, y: 30},
                    {t: end.timestamp, y: 30},
                ],
                label: `${ChartLabels.labels.outOfRange.value}-${start.timestamp}`,
                yAxisID: "y",
                fill: true,
                borderColor: "#CCCCCC",
                backgroundColor: pattern.draw("diagonal-right-left", "#CCCCCC"),
                pointBorderColor: 'rgba(0, 0, 0, 0)',
                pointBackgroundColor: 'rgba(0, 0, 0, 0)',
                pointHoverBackgroundColor: 'rgba(0, 0, 0, 0)',
                pointHoverBorderColor: 'rgba(0, 0, 0, 0)'
            }
        );
        return 0;
    });
    
    graphData.datasets.push({
        label: ChartLabels.labels.respirationLowError.value,
        yAxisID: "y",
        data: respirationLowError,
        borderColor: "#DC4066",
        fill: false,
        tension: 0,
        pointRadius: 0,
        spanGaps: false,
        pointBorderColor: 'rgba(0, 0, 0, 0)',
        pointBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBorderColor: 'rgba(0, 0, 0, 0)'
    });
    graphData.datasets.push({
        label: ChartLabels.labels.respirationHighError.value,
        yAxisID: "y",
        data: respirationHighError,
        borderColor: "#DC4066",
        fill: false,
        tension: 0,
        pointRadius: 0,
        spanGaps: false,
        pointBorderColor: 'rgba(0, 0, 0, 0)',
        pointBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBorderColor: 'rgba(0, 0, 0, 0)'
    });
    graphData.datasets.push({
        label: ChartLabels.labels.respiration.value,
        yAxisID: "y",
        data: props.respiration,
        borderColor: "#4AA1BC",
        tension: 0.4,
        fill: false,
        pointRadius: 0,
        pointBorderColor: 'rgba(0, 0, 0, 0)',
        pointBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBackgroundColor: 'rgba(0, 0, 0, 0)',
        pointHoverBorderColor: 'rgba(0, 0, 0, 0)',
        borderWidth: 2,
        borderDash: [],
        borderDashOffset: 0.0,
        borderCapStyle: 'butt',
        borderJoinStyle: 'miter',
    });
    graphData.datasets.push(
        {
            label: ChartLabels.labels.movement.value,
            yAxisID: "y1",
            data: props.movements,
            fill: false,
            tension: 0.4,
            spanGaps: true,
            borderColor: "#56B6A5",
            pointRadius: 0,
            pointHoverRadius: 3,
            borderDash: [5, 5],
            showLine: true,
            pointBorderColor: 'rgba(0, 0, 0, 0)',
            pointBackgroundColor: 'rgba(0, 0, 0, 0)',
            pointHoverBackgroundColor: '#56B6A5',
            pointHoverBorderColor: '#56B6A5'
        });

    return(
        <>
            <div className="row" style={{marginLeft: 0, marginRight: 0}}>
                <div
                    className="col-md-12 patient-graph-container"
                    style={{padding: patientChartArea.padding}}
                >
                    <Line options={graphOptions} data={graphData} />
                </div>
            </div>

            <WhisperWrapper
                tooltip={sleepBarTooltip}
                props={{ placement: "rightEnd", controlId: "control-id-hover", trigger: "hover" }}
            >
                <div className="timeline-container timeline-sleep">
                    <div style={{width: patientChartArea.left + patientChartArea.padding}} className="timeline-label">
                        Sleep/Rest
                    </div>
                    <div style={{width: patientChartArea.width}}>
                        <Timeline
                            data={props.sleepTransitions}
                            startTime={props.startTime}
                            endTime={_endTime}
                        />
                    </div>
                </div>
            </WhisperWrapper>

            <WhisperWrapper
                tooltip={presenceBarTooltip}
                props={{ placement: "rightEnd", controlId: "control-id-hover", trigger: "hover" }}>
                <div className="timeline-container">
                    <div style={{width: patientChartArea.left + patientChartArea.padding}} className="timeline-label">
                        Presence
                    </div>
                    <div style={{width: patientChartArea.width}} className="visit-graph">
                        <Timeline
                            data={props.visitTransitions}
                            startTime={props.startTime}
                            endTime={_endTime}
                        />
                    </div>
                </div>
            </WhisperWrapper>

            <div className="card-body legend-card">
                <div className="legend-block">
                    <div className="legend-item">
                        <span
                            className="legend-item-color legend-square"
                            style={{backgroundColor: "#FFFFFF"}}
                        />
                        Normal RR Range
                    </div>
                    <div className="legend-item">
                        <canvas
                            ref={outOfRangeLegendRef}
                            width={20}
                            height={20}
                            className="legend-item-canvas"
                        />
                        Out of Range (No Data)
                    </div>
                </div>

                <div className="legend-block">
                    <div className="legend-item">
                        <span className="legend-item-color" style={{
                            display: 'inline-block',
                            width: '20px',
                            height: '20px',
                            borderRadius: '50%',
                            border: '2px solid #56B6A5',
                            backgroundColor: 'white'
                        }} />
                        <span className="movement-label">Movement</span>
                    </div>
                    <div className="legend-item">
                        <span
                            className="legend-item-color legend-vertical"
                            style={{backgroundColor: "#4AA1BC"}}
                        />
                        Respiration
                    </div>
                    <div className="legend-item">
                        <span
                            className="legend-item-color legend-vertical"
                            style={{backgroundColor: "#DE504A"}}
                        />
                        Respiration Low/High
                    </div>
                </div>

                <div className="legend-block">
                    <div className="legend-item">
                        <span
                            className="legend-item-color legend-vertical-thick"
                            style={{backgroundColor: "#f1f0ef"}}
                        />
                        Estimated Awake
                    </div>
                    <div className="legend-item">
                        <span
                            className="legend-item-color legend-vertical-thick"
                            style={{backgroundColor: "#4aa1bc"}}
                        />
                        Estimated Asleep
                    </div>
                </div>
            </div>
        </>
        // <div style={{minWidth: '743px', minHeight: '371px'}}>
        //     {/*<h1>Patient Graph</h1>*/}
        //     <Line options={graphOptions} data={graphData} />
        // </div>
    );
}
